import { Model } from '@shared/models/Model';
import { type IndexPublicTransactionIncludes, routes } from '@app/purchase/transaction/routes/routes.public';
import { useTransactionRepository } from '@app/purchase/transaction/repositories/useTransactionRepository';
import type { DataTableHeaders } from '@shared/types/Vuetify';
import type { IModel } from '@shared/types/Model';
import type { MultiObjectInclude, SingleObjectInclude } from '@shared/types/ApiResponse';
import type { IBlock } from '@app/purchase/block/models/Block';
import type { ICertifier } from '@app/land-hold/certifier/models/Certifier.ts';

export interface IPublicTransaction extends IModel {
    transaction_code: string;
    carbon_amount_tco2e: string;
    execution_date: string;
    type: string;
    steward_name: string;
    partner_name: string | null;
    sum_carbon_in_blocks: string;
    uncovered_carbon_amount: string;
    country: string | null;
    state: string | null;
    blocks: MultiObjectInclude<IBlock>;
    certifier: SingleObjectInclude<ICertifier> | null;
}

export class PublicTransaction extends Model implements IPublicTransaction {
    public transaction_code!: string;
    public carbon_amount_tco2e!: string;
    public execution_date!: string;
    public type!: string;
    public steward_name!: string;
    public partner_name!: string | null;
    public sum_carbon_in_blocks!: string;
    public uncovered_carbon_amount!: string;
    public country!: string | null;
    public state!: string | null;
    public blocks!: MultiObjectInclude<IBlock>;
    public certifier!: SingleObjectInclude<ICertifier> | null;

    public constructor(o: IPublicTransaction) {
        super(o);
        Object.assign(this, o);
    }

    public static override headers(): DataTableHeaders<IndexPublicTransactionIncludes> {
        return [
            {
                title: 'Transaction Code',
                key: 'transaction_code',
                link: (item) => useTransactionRepository().query.showPublic(item.id).endpoint,
                fixed: true,
                width: 226.23,
                hasTooltip: () => ({
                    text: 'Registers a company’s carbon offset transaction. <br/><br/>1st block: the organisation that conducted the carbon inventory and verified the sequestration units. <br/><br/>2nd block: the year the transaction took place. <br/><br/>3rd block: characters that form a unique purchase ID.',
                }),
            },
            {
                title: 'Date',
                key: 'execution_date',
                date: true,
                hasTooltip: () => ({
                    text: 'The confirmed date a company completed its purchase of sequestration units for emissions offsetting.',
                }),
            },
            {
                title: 'Carbon (tCO2e)',
                key: 'carbon_amount_tco2e',
                align: 'center',
                hasTooltip: () => ({
                    text: 'The total amount of sequestration units involved in the transaction, measured in metric tons of CO₂ equivalent.',
                }),
            },
            {
                title: 'Steward',
                key: 'steward_name',
                sortable: false,
                hasTooltip: () => ({
                    text: 'The organisation responsible for conducting carbon inventories and verifying the sequestration units used in carbon offset transactions.',
                }),
            },
            {
                title: 'Transaction Type',
                key: 'type',
                hasTooltip: () => ({
                    text: 'In the Carbono Biodiverso Registry there are two pathways to transaction. <br/><br/>‘Sello Querétaro’ is a local carbon tax system that incentivises companies in Querétaro to offset taxable emissions via carbon projects in the Sierra Gorda Biosphere Reserve. <br/><br/>‘Voluntary’ refers to companies with no offset obligations beyond their own CSR commitments.',
                }),
            },
        ];
    }

    public static override routes(): ReturnType<typeof routes> {
        return routes();
    }
}
